import { HttpError } from "@/shared/api/errors"

export async function wrapStep<T>(
	step: string,
	fn: () => Promise<T>,
): Promise<T> {
	try {
		return await fn()
	} catch (error) {
		if (
			isUserCanceled(error) ||
			isPaymentPending(error) ||
			isPurchaseUnavailable(error)
		)
			throw error
		throw new PurchaseStepError(step, error)
	}
}

class PurchaseStepError extends Error {
	readonly step: string

	constructor(step: string, cause: unknown) {
		super(`[${step}] ${describe(cause)}`, { cause })
		this.name = "PurchaseStepError"
		this.step = step
	}
}

export type PurchaseUnavailableReason =
	| "app-banned"
	| "user-banned"
	| "store-outdated"
	| "billing-unavailable"
	| "unknown"

/** The store stops the payment before the payment sheet opens. This is not a bug: show the message for this reason. */
export class PurchaseUnavailableError extends Error {
	readonly code = "PURCHASE_UNAVAILABLE"
	readonly reason: PurchaseUnavailableReason

	constructor(reason: PurchaseUnavailableReason, cause?: unknown) {
		super(`Purchases are unavailable: ${reason}`, { cause })
		this.name = "PurchaseUnavailableError"
		this.reason = reason
	}
}

export function isPurchaseUnavailable(
	error: unknown,
): error is PurchaseUnavailableError {
	return error instanceof PurchaseUnavailableError
}

export function purchaseErrorProps(error: unknown): {
	step: string | null
	code: string | null
} {
	if (error instanceof PurchaseStepError) {
		return { step: error.step, code: errorCode(error.cause) }
	}
	return { step: null, code: errorCode(error) }
}

export function isUserCanceled(error: unknown): boolean {
	return errorCode(error) === "USER_CANCELED"
}

export function isPaymentPending(error: unknown): boolean {
	return errorCode(error) === "PENDING"
}

const BILLING_CONNECTION_MESSAGES = [
	"Google Play returned SERVICE_DISCONNECTED",
	"Google Play returned SERVICE_UNAVAILABLE",
	"Billing client is not connected.",
]

export function isBillingConnectionLost(error: unknown): boolean {
	if (!(error instanceof Error)) return false
	return BILLING_CONNECTION_MESSAGES.some((text) =>
		error.message.startsWith(text),
	)
}

export function isBillingUnavailable(error: unknown): boolean {
	if (!(error instanceof Error)) return false
	return error.message.startsWith("Google Play returned BILLING_UNAVAILABLE")
}

function errorCode(error: unknown): string | null {
	if (error instanceof HttpError) return String(error.status)
	if (!error || typeof error !== "object") return null
	const { code } = error as { code?: unknown }
	if (typeof code === "string" && code.length > 0) return code
	return null
}

function describe(cause: unknown): string {
	if (cause instanceof Error) return `${cause.name}: ${cause.message}`
	if (typeof cause === "string") return cause
	try {
		return JSON.stringify(cause)
	} catch {
		return String(cause)
	}
}
